import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import * as authApi from "@/api/auth";
import { useToast } from "@/contexts/ToastContext";
import type { MfaEnrolStartResponse } from "@/types/auth";

const schema = z.object({ code: z.string().min(6).max(16) });
type Values = z.infer<typeof schema>;

interface Props {
  mfaSession: string;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}

export function MfaEnrol({ mfaSession, onSuccess, onCancel }: Props) {
  const [enrol, setEnrol] = useState<MfaEnrolStartResponse | null>(null);
  const toast = useToast();
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await authApi.startMfaEnrolment({ mfaSession });
        if (!cancelled) setEnrol(data);
      } catch {
        toast.push("No se pudo iniciar el alta de MFA", "error");
        onCancel();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mfaSession, toast, onCancel]);

  if (!enrol) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted">
        Generando QR…
      </div>
    );
  }

  async function onSubmit(values: Values) {
    try {
      await authApi.confirmMfaEnrolment({ mfaSession, code: values.code });
      await onSuccess();
    } catch {
      toast.push("Código inválido", "error");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="card w-full max-w-md space-y-4"
        data-testid="mfa-enrol-form"
      >
        <h2 className="text-xl font-semibold">Alta de MFA</h2>
        <p className="text-sm text-muted-200">
          Escanea el QR con tu app autenticadora (Google Authenticator, Authy,
          1Password) y captura un código para confirmar.
        </p>
        <div className="flex justify-center">
          <img
            alt="QR MFA"
            className="rounded-md border border-border bg-white p-2"
            data-testid="mfa-qr"
            src={`data:image/png;base64,${enrol.qrPngBase64}`}
            width={196}
            height={196}
          />
        </div>
        <details className="text-xs text-muted-200">
          <summary>Mostrar código manual</summary>
          <code className="block mt-2 break-all">{enrol.secret}</code>
        </details>
        <input
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus
          className="input tracking-widest font-mono"
          data-testid="mfa-enrol-code"
          {...register("code")}
        />
        <div className="card border-warning/50 bg-warning/10 text-xs text-warning">
          <strong>Códigos de recuperación:</strong> guárdalos en un lugar seguro;
          son de un solo uso.
          <ul className="mt-2 grid grid-cols-2 gap-1 font-mono">
            {enrol.recoveryCodes.map((c) => (
              <li key={c} className="text-warning">
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost flex-1">
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="mfa-enrol-submit"
            disabled={formState.isSubmitting}
            className="btn-primary flex-1"
          >
            Confirmar
          </button>
        </div>
      </form>
    </div>
  );
}
